import type { WorkspaceSpotlightTourStep } from '../../../shared/ui/common/WorkspaceSpotlightTour';
import {
  detail,
  spotlightStep,
  type QuickFillTourRefs,
} from './quickFillGuideHelpers';

export const buildFindReplacePreviewTourStep = (
  refs: QuickFillTourRefs
): WorkspaceSpotlightTourStep => spotlightStep(
  {
    id: 'find-replace-preview',
    title: 'Review Find and Replace matches',
    focusLabel: 'Matched Revit parameter cells',
    keepSpotlightClear: true,
    compactOnNarrowViewport: true,
    showArrow: false,
    intro: 'Review matched Revit parameter cells before applying replacements.',
    relatedTargets: [
      {
        label: 'Find and Replace actions',
        resolveTarget: () => {
          const actions = refs.findReplaceDialogRef.current?.nextElementSibling;
          return actions instanceof HTMLElement ? actions : null;
        },
        affectsCalloutLayout: false,
      },
    ],
    details: [
      detail('Control the replacement', [
        'Limit the search to one parameter column unless the text has the same meaning across all included columns.',
        'Run Find All, then review each matched row and cell.',
      ]),
      detail('Protect unrelated cells', [
        'Replace only matches that have the intended engineering meaning.',
      ], 'accent'),
    ],
    nextAction: 'Review every match, then replace only the cells you intend to change.',
  },
  refs.findReplaceDialogRef,
  {
    top: 120,
    left: Math.max((window.innerWidth - 520) / 2, 24),
    width: 500,
    height: 420,
  }
);

export const buildSaveReviewTourStep = (
  refs: QuickFillTourRefs
): WorkspaceSpotlightTourStep => spotlightStep(
  {
    id: 'save-review',
    title: 'Review save results before continuing',
    focusLabel: 'Save results and blocked rows',
    keepSpotlightClear: true,
    compactOnNarrowViewport: true,
    showArrow: false,
    intro: 'Confirm that the intended Quick Fill changes were saved successfully before continuing.',
    relatedTargets: [
      {
        label: 'Apply and continue actions',
        targetRef: refs.footerActionsRef,
      },
    ],
    details: [
      detail('What to confirm', [
        'Review warnings, failed items, and blocked rows.',
        'If any result is unexpected, correct it before continuing.',
      ]),
      detail('Do not assume', [
        'A completed bulk action does not guarantee that all changes were saved successfully.',
      ], 'caution'),
    ],
    nextAction: 'Continue only when the save results match the intended changes.',
  },
  refs.saveReviewDialogRef,
  {
    top: 210,
    left: Math.max((window.innerWidth - 660) / 2, 24),
    width: Math.min(620, window.innerWidth - 48),
    height: 372,
  }
);
